import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form-latest';

import { _notNil } from '@/littledash';

import type { FormBuilderFormData } from '@/components/FormBuilder/FormBuilder.model';
import {
  FieldTypes,
  groupFieldTypeOptions,
  groupTitleValue,
  showSelectField,
} from '@/components/FormBuilder/Item.utils';
import SelectBuilder from '@/components/FormBuilder/SelectBuilder/SelectBuilder';
import Checkbox from '@/components/UI/FormElements/Checkbox';
import ReactSelect from '@/components/UI/Select';

interface FormBuilderColumn {
  id: string;
  title: string;
  width: number;
}

interface FormBuilderItemProps {
  id: string;
  index: number;
  columns: Array<FormBuilderColumn>;
  onFieldTypeChange: (value: string) => void;
  type?: string;
  label?: string;
  hint?: string;
  options?: Array<{ label: string; value: string }>;
  required?: boolean;
}

const FormBuilderItem: FC<FormBuilderItemProps> = ({
  index,
  columns,
  onFieldTypeChange,
  type: typeFromProps,
  label: labelFromProps,
  hint: hintFromProps,
  options: optionsFromProps,
  required: requiredFromProps,
}) => {
  const {
    control,
    register,
    unregister,
    formState: { errors },
  } = useFormContext<FormBuilderFormData>();

  const labelFieldName = `formItems.${index}.label` as const;
  const typeFieldName = `formItems.${index}.type` as const;
  const hintFieldName = `formItems.${index}.hint` as const;
  const optionsFieldName = `formItems.${index}.options` as const;
  const requiredFieldName = `formItems.${index}.required` as const;

  const hasLabelError = _notNil(errors?.formItems?.[index]?.label);
  const hasFieldTypeError = _notNil(errors?.formItems?.[index]?.type);

  const fieldTypesWithNoHintText = [groupTitleValue];
  const fieldTypesWithNoRequiredCheckbox = [groupTitleValue];

  const fieldType = useWatch({
    control,
    name: typeFieldName,
    defaultValue: typeFromProps,
  });

  const fieldLabel = useWatch({
    control,
    name: labelFieldName,
    defaultValue: labelFromProps,
  });

  const fieldHint = useWatch({
    control,
    name: hintFieldName,
    defaultValue: hintFromProps,
  });

  const fieldOptions = useWatch({
    control,
    name: optionsFieldName,
    defaultValue: optionsFromProps,
  });

  const fieldRequired = useWatch({
    control,
    name: requiredFieldName,
    defaultValue: requiredFromProps ?? true,
  });

  useEffect(() => {
    if (fieldType !== FieldTypes.select && fieldType !== FieldTypes.multi_select && fieldType !== FieldTypes.radio) {
      unregister(optionsFieldName);
    }
  }, [unregister, fieldType, optionsFieldName]);

  const defaultSelectValue = {
    label: 'Select...',
    value: '',
  };

  const getColumnById = (id: string): FormBuilderColumn | undefined => columns.find((column) => column.id === id);
  const getWidth = (id: string): number | undefined => getColumnById(id)?.width;

  const buildSelectStyles = (hasErrors: boolean) => ({
    control: (styles: Record<string, unknown>) => ({
      ...styles,
      border: hasErrors ? '2px solid red !important' : styles.border,
      '&:hover': {
        border: hasErrors ? '2px solid red !important' : styles.border,
      },
    }),
  });

  const getFieldTypeDefaultValue = (ft: string) =>
    ft === FieldTypes.radio
      ? groupFieldTypeOptions.find((option) => option.value === FieldTypes.select)
      : groupFieldTypeOptions.find((option) => option.value === ft);

  return (
    <div className="flex flex-row" data-test-component="FormBuilderItem" data-test-element="container">
      <div style={{ width: getWidth('fieldType') }} className="mr5">
        <div className="flex flex-column" data-test-element="field-type-select-container">
          <Controller
            defaultValue={
              fieldType
                ? (getFieldTypeDefaultValue(fieldType)?.value ?? defaultSelectValue.value)
                : defaultSelectValue.value
            }
            name={typeFieldName}
            rules={{
              required: 'This field is required',
            }}
            control={control}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                customStyles={buildSelectStyles(hasFieldTypeError)}
                value={value ? getFieldTypeDefaultValue(String(value)) : undefined}
                options={groupFieldTypeOptions}
                isMulti={false}
                onChange={(selected) => {
                  const next =
                    selected !== null && selected !== undefined && 'value' in selected
                      ? String((selected as { value: string }).value)
                      : '';
                  onFieldTypeChange(next);
                  onChange(next);
                }}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={typeFieldName}
            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
      </div>
      <div style={{ width: getWidth('fieldProperties') }} className="mr5">
        {fieldType ? (
          <div>
            {showSelectField(fieldType) ? (
              <SelectBuilder
                names={{
                  label: labelFieldName,
                  options: optionsFieldName,
                }}
                defaultValue={{
                  label: fieldLabel ?? '',
                  options: fieldOptions ?? [{ label: '', value: '' }],
                }}
              />
            ) : (
              <div className="flex flex-column">
                <input
                  {...register(labelFieldName, { required: 'This field is required' })}
                  defaultValue={fieldLabel}
                  type="text"
                  data-test-element="label-field-input"
                  style={{ marginBottom: 0 }}
                  className={cn({ input__error: hasLabelError })}
                  placeholder="Label"
                />
                <ErrorMessage
                  errors={errors}
                  name={labelFieldName}
                  render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
                />
              </div>
            )}
            {!fieldTypesWithNoHintText.includes(fieldType) && (
              <textarea
                {...register(hintFieldName, { maxLength: { value: 5000, message: 'Maximum of 5000 characters' } })}
                defaultValue={fieldHint}
                data-test-element="hint-field-textarea"
                placeholder="Hint text (optional)"
                className="mt3"
                style={{ resize: 'vertical' }}
              />
            )}
            {!fieldTypesWithNoRequiredCheckbox.includes(fieldType) && (
              <Controller
                defaultValue={fieldRequired}
                name={requiredFieldName}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    onChange={(e) => {
                      onChange(e.target.checked);
                    }}
                    label="Required"
                    name={requiredFieldName}
                    checked={value === true}
                  />
                )}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FormBuilderItem;
